import { useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { productsService, type ProductView, type UpdateProductRequest } from '../../../../../services/products/products.service';
import { toHebrewApiErrorMessage } from '../../../../../services/index/index.service';
import { ALLERGEN_SUGGESTIONS, TAG_SUGGESTIONS } from '../../orders/product-info-modal/product-info.constants';

export type ProductDescriptionModalProps = {
  product: ProductView;
  products: ProductView[];
  employeeSession: string | null;
  onClose: () => void;
};

type DescriptionForm = {
  description: string;
  staffNote: string;
  ingredients: string[];
  allergens: string[];
  tags: string[];
};

export function useProductDescriptionController({ product, products, employeeSession, onClose }: ProductDescriptionModalProps) {
  const [baseline] = useState<DescriptionForm>(() => ({
    description: product.description ?? '', staffNote: product.staffNote ?? '',
    ingredients: [...product.ingredients], allergens: [...product.allergens], tags: [...product.tags],
  }));
  const [form, setForm] = useState(baseline);
  const [isSaving, setIsSaving] = useState(false);
  const saveLock = useRef(false);
  const allergenOptions = Array.from(new Set([...ALLERGEN_SUGGESTIONS, ...products.flatMap((entry) => entry.allergens)]));
  const tagOptions = Array.from(new Set([...TAG_SUGGESTIONS, ...products.flatMap((entry) => entry.tags)]));
  const setField = <Key extends keyof DescriptionForm>(key: Key, value: DescriptionForm[Key]) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  const handleSave = async () => {
    if (saveLock.current) return;
    const patch: UpdateProductRequest = {};
    for (const key of ['description', 'staffNote'] as const) {
      const value = form[key].trim();
      if (value !== baseline[key]) patch[key] = value || null;
    }
    for (const key of ['ingredients', 'allergens', 'tags'] as const) {
      if (form[key].join('\n') !== baseline[key].join('\n')) patch[key] = form[key];
    }
    if (Object.keys(patch).length === 0 || !employeeSession) {
      onClose();
      return;
    }
    saveLock.current = true;
    setIsSaving(true);
    try {
      await productsService.update(product.id, patch, employeeSession);
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(message), autoClose: 3000 });
    } finally {
      saveLock.current = false;
      setIsSaving(false);
    }
  };

  return { form, setField, isSaving, handleSave, allergenOptions, tagOptions };
}